import React, { useContext, useEffect, useState } from "react";
import TaxContexts from "../../contexts/tax-contexts";
import moment from "moment";
import { Table, Button, Form, Input, Divider, Col, Row } from "antdV4";
import { Modal, notification } from "antd";
import { axios, getToken, URL_API } from "../../../../../utils";

const PreviewImportModal = () => {
  const source = axios.CancelToken.source();
  const { state, dispatch } = useContext(TaxContexts);
  const previewImportModal = state.previewImportModal || { visible: false };
  const taxInfo = state.taxInfo || {};
  const pageSize = 20;

  const [importPage, setImportPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const values = await form.validateFields();

      const { data } = await axios({
        method: "post",
        baseURL: URL_API,
        url: `/import-document/tax/import`,
        headers: { Authorization: "Bearer " + getToken() },
        data: {
          ...values,
          tax_list: taxInfo.tax_list,
          file_name: taxInfo.file_name,
          sheet_name: taxInfo.sheet_name,
        },
        cancelToken: source.token,
      });

      dispatch({
        type: "setTaxInfo",
        payload: { ...data },
      });

      // ✅ ปิด PreviewImportModal
      dispatch({
        type: "setPreviewImportModal",
        payload: { visible: false },
      });

      // ✅ เปิด Modal ตัวใหม่ (เช่น modal แสดงผลลัพธ์การ import)
      dispatch({
        type: "setModalImportStatus",
        payload: { visible: true },
      });
    } catch (err) {
      if (err?.errorFields?.length) {
        const firstError = err.errorFields[0];
        const message = firstError.errors[0] || "กรุณากรอกข้อมูลให้ครบถ้วน";
        notification.error({
          message: "ข้อมูลไม่ครบถ้วน",
          description: message,
          placement: "topRight",
          duration: 5,
        });
      } else {
        // ✅ กรณี error จาก axios
        Modal.error({
          title: "ผิดพลาด",
          content: err.response?.data?.message || err.message,
          okText: "ตกลง",
          zIndex: 1080,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (previewImportModal.visible) {
      setImportPage(1);
      form.setFieldsValue(taxInfo);
    }
  }, [previewImportModal.visible, form, taxInfo]);

  return (
    <Modal
      title="ยืนยันการทำรายการ"
      visible={previewImportModal.visible}
      width="80%"
      zIndex={1080}
      footer={[
        <Button
          key="cancel"
          onClick={() =>
            dispatch({
              type: "setPreviewImportModal",
              payload: { visible: false },
            })
          }
          style={{ marginRight: 8 }}
        >
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          บันทึก
        </Button>,
      ]}
      onCancel={() =>
        dispatch({
          type: "setPreviewImportModal",
          payload: { visible: false },
        })
      }
    >
      <Form form={form} className="mb-3" layout="vertical" scrollToFirstError>
        <Divider orientation="left" style={{ marginTop: 12, marginBottom: 12 }}>
          ข้อมูลเอกสารภาษี
        </Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="ชื่อหัวข้อภาษี"
              name="tax_title"
              rules={[{ required: true, message: "กรุณากรอกชื่อหัวข้อภาษี" }]}
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="ปีภาษี (พ.ศ.)"
              name="tax_year"
              rules={[
                { required: true, message: "กรุณากรอกปีภาษี" },
                {
                  pattern: /^[0-9]+$/,
                  message: "กรุณากรอกเฉพาะตัวเลขเท่านั้น",
                },
                {
                  max: 4,
                  message: "กรุณากรอกไม่เกิน 4 ตัวอักษร",
                },
              ]}
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="ชื่อบริษัท"
              name="company_name"
              rules={[{ required: true, message: "กรุณากรอกชื่อบริษัท" }]}
              style={{ marginBottom: 12 }}
            >
              <Input disabled />
            </Form.Item>
          </Col>

          <Col span={10}>
            <Form.Item
              label="ที่อยู่บริษัท"
              name="company_address"
              rules={[{ required: true, message: "กรุณากรอกที่อยู่บริษัท" }]}
              style={{ marginBottom: 12 }}
            >
              <Input disabled />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="รหัสผู้เสียภาษีบริษัท"
              name="company_tax"
              rules={[
                { required: true, message: "กรุณากรอกรหัสผู้เสียภาษีบริษัท" },
              ]}
              style={{ marginBottom: 12 }}
            >
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 8, marginBottom: 12 }}>
          ผู้ออกเอกสาร
        </Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="รหัสพนักงาน"
              name="account_code"
              rules={[{ required: true, message: "กรุณากรอกรหัสพนักงาน" }]}
              style={{ marginBottom: 12 }}
            >
              <Input disabled />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="ชื่อพนักงาน"
              name="account_name"
              rules={[{ required: true, message: "กรุณากรอกชื่อพนักงาน" }]}
              style={{ marginBottom: 12 }}
            >
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        dataSource={
          taxInfo.tax_list?.map((item, idx) => ({
            ...item,
            key: idx.toString(),
          })) || []
        }
        rowKey="key"
        pagination={{
          current: importPage,
          pageSize: pageSize,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ`,
          total: taxInfo.tax_list?.length || 0,
          size: "default",
          onChange: (currPage) => setImportPage(currPage),
        }}
      >
        <Table.Column
          title="#"
          key="index"
          render={(_, __, index) => (importPage - 1) * pageSize + index + 1}
        />
        <Table.Column title="เลขที่เอกสาร" dataIndex="document_no" />
        <Table.Column
          title="วันที่เอกสาร"
          dataIndex="document_date"
          render={(date) => moment(date).format("DD/MM/YYYY")}
        />
        <Table.Column title="รหัสสมาชิก" dataIndex="partner_code" />
        <Table.Column title="ชื่อ - นามสกุล" dataIndex="partner_name" />
        <Table.Column title="ที่อยู่" dataIndex="partner_address" />
        <Table.Column title="เลขประจำตัวผู้เสียภาษี" dataIndex="partner_tax" />
        <Table.Column title="เบอร์โทร" dataIndex="partner_mobile" />
        <Table.Column title="ประเภทภาษี" dataIndex="tax_type" />
        <Table.Column title="ยอดรวม" dataIndex="total_amount" />
        <Table.Column title="ภาษี" dataIndex="tax_amount" />
        <Table.Column title="ภาษี (Text)" dataIndex="tax_amount_text" />
      </Table>
    </Modal>
  );
};

export default PreviewImportModal;
